import cx from 'clsx';
import {
  selectShowHoverChildren,
  selectZenModeEnabled,
  showHoverChildrenChanged,
  zenModeRestored,
} from 'features/layout/features/config/configSlice';
import { HoverParent } from 'features/layout/features/config/types';
import { useSystemSession } from 'hooks/useSystemSession';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getZenModeSetting } from 'utils/zenMode';

const AUTO_HIDE_DELAY_MS = 4000;

// One countdown per area, shared by every consumer of that area - a per-instance timer would
// hide the children under a pointer that another consumer is still tracking.
const autoHideTimers = new Map<HoverParent, number>();

export const useHoverParent = (area: HoverParent) => {
  const isSystemSession = useSystemSession();

  const showHoverChildren = useAppSelector((state) => selectShowHoverChildren(state, area));
  const zenModeEnabled = useAppSelector(selectZenModeEnabled);

  const isShowingRef = useRef(false);
  const scheduledTimerRef = useRef(0);

  const dispatch = useAppDispatch();

  const showHover = useCallback(() => {
    isShowingRef.current = true;
    dispatch(showHoverChildrenChanged({ show: true, area }));
  }, [dispatch, area]);

  const hideHover = useCallback(() => {
    isShowingRef.current = false;
    dispatch(showHoverChildrenChanged({ show: false, area }));
  }, [dispatch, area]);

  const resetAutoHideTimer = useCallback(() => {
    clearTimeout(autoHideTimers.get(area));
    scheduledTimerRef.current = window.setTimeout(hideHover, AUTO_HIDE_DELAY_MS);
    autoHideTimers.set(area, scheduledTimerRef.current);
  }, [area, hideHover]);

  const cancelAutoHideTimer = useCallback(() => {
    clearTimeout(autoHideTimers.get(area));
    // Leaving the id behind lets a later consumer inherit it and have its live countdown cleared
    // by this one's unmount, which compares ids.
    autoHideTimers.delete(area);
  }, [area]);

  const isShowingHover = useCallback(() => isShowingRef.current, []);

  // A countdown this consumer owns would otherwise outlive it and fire hideHover into whatever
  // tree mounts next.
  useEffect(
    () => () => {
      if (autoHideTimers.get(area) === scheduledTimerRef.current) {
        clearTimeout(scheduledTimerRef.current);
        autoHideTimers.delete(area);
      }
    },
    [area]
  );

  // Mirror Redux state so external writes (e.g. zenModeChanged)
  // don't leave the ref stale and block the onMouseMove recovery.
  useEffect(() => {
    isShowingRef.current = showHoverChildren;
  }, [showHoverChildren]);

  useEffect(() => {
    if (!isSystemSession) {
      showHover();
      resetAutoHideTimer();
    }

    dispatch(zenModeRestored(getZenModeSetting()));
  }, [isSystemSession, showHover, resetAutoHideTimer, dispatch]);

  const className = useMemo(
    () =>
      cx({
        'hover-parent-movement-on': isSystemSession ? true : showHoverChildren,
        'hover-parent-movement-off': isSystemSession ? false : !showHoverChildren,
        'hover-parent-show': !isSystemSession && !zenModeEnabled,
      }),
    [isSystemSession, showHoverChildren, zenModeEnabled]
  );

  const onMouseEnter = useCallback(() => {
    if (isSystemSession) {
      return;
    }

    showHover();
    resetAutoHideTimer();
  }, [isSystemSession, showHover, resetAutoHideTimer]);

  const onMouseMove = useCallback(() => {
    if (isSystemSession) {
      return;
    }

    if (!isShowingRef.current) {
      showHover();
    }

    resetAutoHideTimer();
  }, [isSystemSession, showHover, resetAutoHideTimer]);

  const onMouseLeave = useCallback(() => {
    if (isSystemSession) {
      return;
    }

    cancelAutoHideTimer();
    hideHover();
  }, [isSystemSession, cancelAutoHideTimer, hideHover]);

  return {
    className,
    onMouseEnter,
    onMouseMove,
    onMouseLeave,
    showHover,
    isShowingHover,
    resetAutoHideTimer,
    cancelAutoHideTimer,
  };
};

export const hoverParentStyles = {
  '& .hover-child': {
    opacity: 0,
    transition: 'opacity .2s ease-in',
  },
  '.hover-parent-movement-off & .hover-child': {
    opacity: 0,
  },
  '.hover-parent-show & .hover-child': {
    opacity: '1 !important',
  },
  '.hover-parent-movement-on &:hover .hover-child': {
    opacity: 1,
  },
  '.hover-parent-movement-off &:hover .hover-child': {
    opacity: 0,
  },
  '.hover-parent-movement-on &:hover .hover-child-eager': {
    opacity: 1,
  },
  '.hover-parent-movement-off &:hover .hover-child-eager': {
    opacity: 0,
  },
  '&:focus .hover-child': {
    opacity: 1,
  },
  '&:focus .hover-child-eager': {
    opacity: '1 !important',
  },
  '.hover-parent-movement-off &:focus .hover-child': {
    opacity: 0,
  },
  '.hover-parent-movement-off &:focus .hover-child-eager': {
    opacity: '0 !important',
  },
};
